/**
 * ga4Measurement — host → GA4 measurement ID, the SSOT (AISEO-GA4-001).
 *
 * ## Why this is a HOST map and not a per-app env var
 *
 * The obvious build-time design — `VITE_GA4_MEASUREMENT_ID` in each app's `.env` — is wrong here,
 * and wrong in a way that produces confidently mislabelled data rather than an error.
 *
 * A TENANT site's property is not here (LIFE-SHORTLISTED-001 Q-LS-32, owner ruling 2026-09-28). A
 * tenant site (a vanity domain served by purchase-web's white-label tree) reads its own id from its
 * tenant record (`settings.analytics.ga4MeasurementId`) — tenant data, so a new tenant site needs no
 * code change and platform code names no tenant. This map holds the PLATFORM's own sites: the nine
 * Colony marketing apexes and publishpuffin.com. (Goldmark, ProtectorPuffin and Custom Carvers moved
 * to their tenant configs with the same ids.)
 *
 * Resolving from `location.hostname` also means adding a brand is one entry here rather than a new
 * env var propagated through an app `.env`, a DO app spec, and a deploy — the sort of multi-place
 * change these nine apps have drifted on before.
 *
 * ## Measurement IDs are not secrets
 *
 * A GA4 measurement ID ships inside the page to every visitor; it is a public identifier, not a
 * credential. Committing the map is therefore correct, and it is what makes the mapping reviewable
 * — an env var would hide which brand points at which property.
 *
 * @module @puffin-core/marketing-shell/analytics/ga4Measurement
 */

/**
 * Apex domain → GA4 measurement ID.
 *
 * Created 2026-08-13 against GA account `220936064`. `publishpuffin.com` pre-existed; the other
 * twelve were created together via the Analytics Admin API.
 *
 * **WordPress-fleet sites are deliberately NOT here** (WPFLEET-001 P09, owner ruling 2026-09-17).
 * `garageharmony.com` (`G-6TFRV7Y953`) left this map when the fleet's `wp-fleet-analytics` member
 * became its single emitter: a fleet site's measurement id lives in its manifest
 * (`publishpuffin: fleet/sites/<domain>.yaml` → `analytics`), and `hvacharmony.com` was never
 * listed. An entry here would make `publishpuffin-seo` emit the same property a second time —
 * every page view double-counted, silently. The unit test pins the absence.
 *
 * Keyed on the APEX domain — `www.`, bare apex, and any subdomain all normalise to the same key,
 * so a marketing site reached at `www.` and at the apex reports into ONE property rather than
 * splitting its sessions across two.
 */
export const GA4_MEASUREMENT_IDS = Object.freeze({
  'parcelpuffin.com': 'G-FSW95NE97R',
  'palmpuffin.com': 'G-VYV301TC9Y',
  'patrolpuffin.com': 'G-S5QQ8PL8BP',
  'paydaypuffin.com': 'G-MWD17729ZS',
  'plannerpuffin.com': 'G-09X1JRTC2G',
  'portpuffin.com': 'G-CDENSGTGZH',
  'preppuffin.com': 'G-L4TSQ7BXXR',
  'purchasepuffin.com': 'G-GL0XN17HCR',
  'prospectpuffin.com': 'G-N70QEHCSV4',
  'publishpuffin.com': 'G-DB5EES2HS4',
});

/**
 * Reduce a hostname to the apex key used above.
 *
 * Deliberately a plain last-two-labels rule, not a public-suffix list: every host in this estate is
 * a `<brand>.com`, so a PSL dependency would add weight for no behavioural difference. A future
 * `.co.uk` brand needs a PSL — and would fail loudly here (unmapped ⇒ no tag) rather than
 * misreport.
 *
 * @param {string} hostname
 * @returns {string} apex domain, lower-cased
 */
export function toApexDomain(hostname) {
  const host = String(hostname || '')
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/\.$/, '');
  if (!host) return '';
  const labels = host.split('.');
  return labels.length <= 2 ? host : labels.slice(-2).join('.');
}

/**
 * Resolve the GA4 measurement ID for a hostname.
 *
 * **Returns `undefined` for anything unmapped, and that is the designed behaviour.** `localhost`,
 * preview hosts, `*.ondigitalocean.app` and tunnel URLs must NOT emit into a production property —
 * dev traffic in the real numbers is worse than no dev traffic, because it is invisible once
 * aggregated. An unmapped host simply gets no GA4 transport.
 *
 * @param {string} [hostname] Defaults to `location.hostname` when in a browser.
 * @returns {string|undefined}
 */
export function resolveGa4MeasurementId(hostname) {
  const raw =
    hostname !== undefined
      ? hostname
      : typeof globalThis !== 'undefined' && globalThis.location
        ? globalThis.location.hostname
        : '';
  return GA4_MEASUREMENT_IDS[toApexDomain(raw)];
}
